import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { updateMessages, toggleSpinner } from 'apps/auth/actions/appAction';
import { changeFormFieldValue, setFieldError } from 'apps/auth/actions/formAction';
import VerifyAddressInfoPage from 'apps/auth/components/antifraud/addressValidation/VerifyAddressInfoPage';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import FormComponent from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace, getSortFields } from 'apps/auth/components/form-multi/utils';
import BasePage from 'apps/auth/components/utils/BasePage';
import { ADRESS_SCREEN_CONTINUE_CLICKED, ADRESS_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { COUNTRY_OF_RESIDENCE, FLOW } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { PAYMENT } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PROFILE_EDIT_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_ANTIFRAUD_UPDATE_ADDRESS, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';
import type { Country } from 'apps/auth/types/globals';

const INFO_SCREEN = 'info';
const FIELDS_SCREEN = 'fields';
type Screen = typeof INFO_SCREEN | typeof FIELDS_SCREEN;

interface AddressFormField {
    name: string;
    visible?: boolean;
    isError?: boolean;
    [key: string]: unknown;
}

interface AddressForm {
    name: string;
    fields: Record<string, AddressFormField>;
    errors?: Record<string, string>;
}

interface VerifyAddressPageProps {
    changeFormFieldValue: (formName: string, value: unknown, fieldName: string) => void;
    setFieldError: (formName: string, fieldName: string, error: unknown) => void;
    updateMessages: (messages: unknown) => void;
    toggleSpinner: (isShow: boolean) => void;
}

type AddressActionResponse = {
    extra?: {
        errors?: Record<string, string>;
    };
    messageBodyMessages?: unknown;
    redirectTo?: string;
};

type VerifyAddressFieldsMessages = {
    title?: string;
    subTitle?: string;
    continueButton?: string;
};

const FORM_ID = 'antifraud_update_address';

const SUCCESS_REDIRECT_DELAY_MS = 2000;

const Form = FormComponent as unknown as React.ComponentType<Record<string, unknown>>;

const VerifyAddressPage: React.FC<VerifyAddressPageProps> = (props) => {
    const [screen, setScreen] = useState<Screen>(INFO_SCREEN);
    const { payId, phoneCountry } = useExtra<{ payId?: string; phoneCountry?: Country & { nameEn?: string } }>() ?? {};
    const form = useForm<AddressForm>(PROFILE_EDIT_FORM_NAME);
    const { title, subTitle, continueButton } = usePageMessages<VerifyAddressFieldsMessages>() ?? {};

    useEffect(() => {
        if (screen === FIELDS_SCREEN) {
            amplitude.getInstance().logEvent(ADRESS_SCREEN_SHOWN, {
                [COUNTRY_OF_RESIDENCE]: phoneCountry?.nameEn,
                [FLOW]: PAYMENT,
            });
        }
    }, [phoneCountry?.nameEn, screen]);

    if (screen === INFO_SCREEN) {
        return (
            <VerifyAddressInfoPage
                onContinueClick={() => setScreen(FIELDS_SCREEN)}
                toggleSpinner={props.toggleSpinner}
                setFieldError={props.setFieldError}
                form={form}
            />
        );
    }

    const fields = form?.fields ?? {};
    const beforeSubmit = () => (form ? clearLeadingSpace(form.fields, form.name, props.changeFormFieldValue) : undefined);

    const submitHandler = (_responseFields: unknown, json: AddressActionResponse) => {
        if (json) {
            const errors = json?.extra?.errors;
            if (json.messageBodyMessages) {
                props.updateMessages(json.messageBodyMessages);
            }
            if (errors && Object.keys(errors).length) {
                Object.keys(errors).forEach((name) => props.setFieldError(PROFILE_EDIT_FORM_NAME, name, errors[name]));
            } else {
                setTimeout(() => {
                    props.toggleSpinner(true);
                    window.location.replace(AUTH_SEND_MAIN_LINK);
                }, SUCCESS_REDIRECT_DELAY_MS);
            }
        }
    };

    return (
        <CommonPage isBackButton leftCloseIcon onClickIcon={() => setScreen(INFO_SCREEN)} hasBottomPanel>
            <HeaderPanel title={title} subtitle={subTitle} />
            <Form
                id={FORM_ID}
                formName={PROFILE_EDIT_FORM_NAME}
                action={`${AUTH_ANTIFRAUD_UPDATE_ADDRESS}/${payId}/action`}
                beforeSubmit={beforeSubmit}
                onSubmitHandler={submitHandler}
                getCanonicalPath={true}
            >
                <fieldset>
                    {getSortFields(fields)
                        .filter((field: AddressFormField) => field.visible)
                        .map((field: AddressFormField) => (
                            <Row indent="24px" key={field.name}>
                                <InputAutoDetect field={field} formName={PROFILE_EDIT_FORM_NAME} country={phoneCountry} firstInputNotFocused />
                            </Row>
                        ))}
                </fieldset>
                <FixedBottomPanel isRightAligned>
                    <Button
                        type="submit"
                        raised="true"
                        caption={continueButton}
                        onClick={() => {
                            amplitude.getInstance().logEvent(ADRESS_SCREEN_CONTINUE_CLICKED, {
                                [FLOW]: PAYMENT,
                            });
                        }}
                    />
                </FixedBottomPanel>
            </Form>
        </CommonPage>
    );
};

export default connect(null, { changeFormFieldValue, setFieldError, updateMessages, toggleSpinner })(BasePage(VerifyAddressPage));
